import { useMemo } from 'react';
import type {
  AgentTaskCard,
  AgentTaskGroup,
} from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import type { PricingTaskRunSummaryFieldsFragment } from '@/types';

const toCard = (raw: PricingTaskRunSummaryFieldsFragment): AgentTaskCard => ({
  acceptedRecommendationsCount: raw.accepted_recommendations_count,
  autoPublishAt: raw.auto_publish_at ?? null,
  description: raw.description ?? '',
  lastCompletedAt: raw.last_completed_at ?? null,
  name: raw.name,
  overriddenRecommendationsCount: raw.overridden_recommendations_count,
  rejectedRecommendationsCount: raw.rejected_recommendations_count,
  sortOrder: raw.sort_order,
  taskTypeId: raw.task_type_id,
  totalRecommendationsCount:
    raw.accepted_recommendations_count +
    raw.overridden_recommendations_count +
    raw.pending_recommendations_count +
    raw.rejected_recommendations_count,
  weeklyGPImpactInMicros: raw.weekly_gp_impact_in_micros,
});

export const useTaskRunGroupsTransformer = (
  taskRuns: PricingTaskRunSummaryFieldsFragment[] | undefined,
): AgentTaskGroup[] =>
  useMemo(() => {
    if (!taskRuns?.length) {
      return [];
    }

    const groupMap = new Map<string, AgentTaskGroup>();
    for (const raw of taskRuns) {
      const card = toCard(raw);
      const groupKey = raw.task_group ?? '';
      const existing = groupMap.get(groupKey);
      if (existing) {
        existing.cards.push(card);
      } else {
        groupMap.set(groupKey, { cards: [card], taskGroup: groupKey });
      }
    }

    const groups = [...groupMap.values()];
    for (const group of groups) {
      group.cards.sort((a, b) => a.sortOrder - b.sortOrder);
    }
    return groups.sort((a, b) => a.taskGroup.localeCompare(b.taskGroup));
  }, [taskRuns]);
